import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import HelperNote from '../components/HelperNote';
import UserBadges from '../components/UserBadges';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProfileSettings() {
  const { user, setUser, checkAuth } = useContext(AuthContext);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [name, setName] = useState(user?.name || ''); // Ad soyad için state
  const [editingName, setEditingName] = useState(false); // Ad soyad düzenleme modu
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Yönetici',
      'proje_yoneticisi': 'Proje Yöneticisi',
      'takim_lideri': 'Takım Lideri',
      'takim_uyesi': 'Takım Üyesi',
      // Backward compatibility
      'project_manager': 'Proje Yöneticisi',
      'team_leader': 'Takım Lideri',
      'team_member': 'Takım Üyesi'
    };
    return roleMap[role] || role;
  };

  const handleNameUpdate = async () => {
    // Validation: Ad soyad boş olamaz
    if (!name.trim()) {
      toast.error('Ad soyad boş bırakılamaz');
      return;
    }

    setLoading(true);
    try {
      await axios.patch(`${API}/users/${user.id}`, {
        name: name.trim()
      });
      
      // Kullanıcı bilgilerini güncelle
      setUser({ ...user, name: name.trim() });
      setEditingName(false);
      toast.success('Ad soyad başarıyla güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ad soyad güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNameEdit = () => {
    setName(user.name); // Eski değere geri dön
    setEditingName(false);
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece resim dosyaları yüklenebilir (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Check file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('Resim boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/users/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Immediately update user context with new picture URL
      const updatedUser = { ...user, picture: response.data.picture };
      setUser(updatedUser);
      
      toast.success('Profil resminiz güncellendi');
      setPreviewImage(null);
      
      // Refresh full user data in background
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resim yüklenemedi');
      setPreviewImage(null);
    } finally {
      setUploadingPicture(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm('Profil resminizi silmek istediğinizden emin misiniz?')) {
      return;
    }

    setUploadingPicture(true);
    try {
      await axios.delete(`${API}/users/profile-picture`);
      
      // Immediately update user context - remove picture
      const updatedUser = { ...user };
      delete updatedUser.picture;
      setUser(updatedUser);
      
      toast.success('Profil resmi silindi');
      
      // Refresh full user data in background
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resim silinemedi');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    
    // Güçlü şifre kontrolü (backend'deki ile uyumlu)
    if (passwordData.newPassword.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      return;
    }
    if (!/[A-Z]/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 büyük harf içermelidir');
      return;
    }
    if (!/[a-z]/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 küçük harf içermelidir');
      return;
    }
    if (!/\d/.test(passwordData.newPassword)) {
      toast.error('Şifre en az 1 rakam içermelidir');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast.success('Şifreniz başarıyla değiştirildi');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          Profil Ayarları
        </h1>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">Hesap bilgilerinizi ve şifrenizi yönetin</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
        {/* Profil Bilgileri */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-cyan-100 p-3 rounded-full">
              <User className="w-6 h-6 text-cyan-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Profil Bilgileri</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-gray-700">Ad Soyad</Label>
              {editingName ? (
                <div className="mt-1 space-y-2">
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ad Soyad"
                    className="w-full"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleNameUpdate} 
                      disabled={loading}
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                    <Button 
                      onClick={handleCancelNameEdit} 
                      disabled={loading}
                      size="sm"
                      variant="outline"
                    >
                      İptal
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                  <p className="text-gray-900 font-medium">{user.name}</p>
                  <Button 
                    onClick={() => setEditingName(true)} 
                    size="sm"
                    variant="ghost"
                    className="text-cyan-600 hover:text-cyan-700"
                  >
                    Düzenle
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label className="text-gray-700">E-posta</Label>
              <div className="mt-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                <Mail className="w-4 h-4 text-gray-400 mr-2" />
                <p className="text-gray-900">{user.email}</p>
              </div>
            </div>

            <div>
              <Label className="text-gray-700">Rol</Label>
              <div className="mt-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                <Shield className="w-4 h-4 text-gray-400 mr-2" />
                <p className="text-gray-900">{getRoleDisplayName(user.role)}</p>
              </div>
            </div>

            {/* Profil Resmi Yükleme */}
            <div>
              <Label className="text-gray-700">Profil Resmi</Label>
              <div className="mt-2 flex items-center space-x-4">
                <div className="relative">
                  {previewImage ? (
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      className="w-20 h-20 rounded-full border-2 border-cyan-500 object-cover"
                    />
                  ) : user.picture ? (
                    <img 
                      src={user.picture.startsWith('http') ? user.picture : `${BACKEND_URL}${user.picture}`}
                      alt={user.name} 
                      className="w-20 h-20 rounded-full border-2 border-gray-200 object-cover"
                      onError={(e) => {
                        console.error('Image load error:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-2 border-gray-200 bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center">
                      <User className="w-10 h-10 text-cyan-600" />
                    </div>
                  )}
                  {uploadingPicture && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    id="profile-picture-upload"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleProfilePictureUpload}
                    disabled={uploadingPicture}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingPicture}
                    onClick={() => document.getElementById('profile-picture-upload').click()}
                  >
                    {uploadingPicture ? 'Yükleniyor...' : 'Resim Yükle'}
                  </Button>
                  {user.picture && !user.picture.includes('googleusercontent.com') && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteProfilePicture}
                      disabled={uploadingPicture}
                      className="text-red-600 hover:text-red-700 ml-2"
                    >
                      Resmi Sil
                    </Button>
                  )}
                  <HelperNote variant="info" className="mt-2">
                    JPEG, PNG, GIF veya WEBP. Max 5MB.
                  </HelperNote>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Şifre Değiştirme */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-purple-100 p-3 rounded-full">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Şifre Değiştir</h2>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Mevcut Şifre</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Mevcut şifrenizi girin"
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showCurrentPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="newPassword">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Yeni şifrenizi girin"
                  required
                  disabled={loading}
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showNewPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <HelperNote variant="info" className="mt-2">Min. 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam</HelperNote>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Yeni Şifre (Tekrar)</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Yeni şifrenizi tekrar girin"
                  required
                  disabled={loading}
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              disabled={loading}
            >
              {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
            </Button>
          </form>
        </div>
      </div>

      {/* Güvenlik Bilgileri */}
      <div className="glass rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Güvenlik İpuçları</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Güçlü Şifre Kullanın</p>
              <p className="text-xs text-gray-600 mt-1">Min. 8 karakter, büyük/küçük harf ve rakam</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Şifrenizi Paylaşmayın</p>
              <p className="text-xs text-gray-600 mt-1">Kimseyle şifrenizi paylaşmayın</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Düzenli Değiştirin</p>
              <p className="text-xs text-gray-600 mt-1">Şifrenizi düzenli aralıklarla güncelleyin</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rozetlerim */}
      <div className="mt-4 sm:mt-6">
        <UserBadges />
      </div>
    </div>
  );
}
